import { Component } from '@theme/component';
import { ProductSelectEvent } from '@shopify/events';

/** Size key reserved for the off-catalogue enquiry option. */
const CUSTOM_SIZE = 'custom-size';

/**
 * Plan selector for the Clean Air Zone subscription product.
 *
 * Checkout happens on an external gateway, so this component never touches the
 * Shopify cart. Every price panel and every checkout link is pre-rendered by
 * Liquid and revealed with CSS, which means the call to action is a plain
 * anchor and works with JavaScript disabled.
 *
 * What is left for JS: mirror the selection onto ARIA attributes, keep
 * ?variant= in the URL so a refresh or a shared link lands on the same choice,
 * and ask the media gallery to follow the selected home size.
 *
 * @typedef {object} PlanSelectorRefs
 * @property {HTMLScriptElement} config
 * @extends Component<PlanSelectorRefs>
 */
class PlanSelectorComponent extends Component {
  requiredRefs = ['config'];

  /** @type {{ sizes: Record<string, { monthly: number, installment: number, upfront: number }> }} */
  #config = { sizes: {} };

  /** @type {AbortController | null} */
  #mediaController = null;

  connectedCallback() {
    super.connectedCallback();

    try {
      this.#config = JSON.parse(this.refs.config.textContent || '{}');
    } catch (error) {
      console.error('[plan-selector] Could not parse config', error);
    }

    this.#syncAria();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#mediaController?.abort();
  }

  get size() {
    return this.dataset.size || '';
  }

  get term() {
    return this.dataset.term || 'monthly';
  }

  get payment() {
    return this.dataset.payment || 'installment';
  }

  /** True when the off-catalogue size is selected — no variant exists for it. */
  get isCustomSize() {
    return this.size === CUSTOM_SIZE;
  }

  get #entry() {
    return this.#config.sizes?.[this.size] ?? null;
  }

  /** The variant behind the current selection, used only for URL and media. */
  get variantId() {
    const entry = this.#entry;
    if (!entry) return 0;
    if (this.term === 'annual') {
      return this.payment === 'upfront'
        ? entry.upfront || entry.installment || 0
        : entry.installment || entry.upfront || 0;
    }
    return entry.monthly || 0;
  }

  /* ------------------------------------------------------------------ */
  /* Handlers — wired via on:click in the Liquid template                */
  /* ------------------------------------------------------------------ */

  /** @param {Event} event */
  selectSize(event) {
    const key = this.#optionValue(event, 'sizeOption');
    if (!key || key === this.size) return;

    this.dataset.size = key;

    // Not every size necessarily has annual variants.
    if (!this.isCustomSize && this.term === 'annual' && !this.#entry?.installment && !this.#entry?.upfront) {
      this.dataset.term = 'monthly';
    }

    this.#afterChange({ variantChanged: true });
  }

  /** @param {Event} event */
  selectTerm(event) {
    const key = this.#optionValue(event, 'termOption');
    if (!key || key === this.term) return;
    this.dataset.term = key;
    this.#afterChange();
  }

  /** @param {Event} event */
  selectPayment(event) {
    const key = this.#optionValue(event, 'paymentOption');
    if (!key || key === this.payment) return;
    this.dataset.payment = key;
    this.#afterChange();
  }

  /**
   * @param {Event} event
   * @param {string} datasetKey
   * @returns {string | undefined}
   */
  #optionValue(event, datasetKey) {
    const target = event.target;
    if (!(target instanceof Element)) return undefined;
    const attr = `data-${datasetKey.replace(/[A-Z]/g, (c) => `-${c.toLowerCase()}`)}`;
    const button = target.closest(`[${attr}]`);
    if (!(button instanceof HTMLElement)) return undefined;
    return button.dataset[datasetKey];
  }

  /** @param {{ variantChanged?: boolean }} [options] */
  #afterChange({ variantChanged = false } = {}) {
    this.#syncAria();
    this.#syncUrl();
    if (variantChanged) this.#syncProductMedia();

    this.dispatchEvent(
      new CustomEvent('plan-selector:change', {
        bubbles: true,
        detail: {
          size: this.size,
          term: this.term,
          payment: this.payment,
          variantId: this.variantId,
        },
      })
    );
  }

  /** Mirrors state onto aria-checked / aria-pressed for assistive tech. */
  #syncAria() {
    /** @type {[string, string, string][]} */
    const groups = [
      ['data-size-option', 'sizeOption', this.size],
      ['data-term-option', 'termOption', this.term],
      ['data-payment-option', 'paymentOption', this.payment],
    ];

    for (const [attr, key, current] of groups) {
      for (const el of this.querySelectorAll(`[${attr}]`)) {
        if (!(el instanceof HTMLElement)) continue;
        const active = el.dataset[key] === current;
        const prop = el.getAttribute('role') === 'radio' ? 'aria-checked' : 'aria-pressed';
        el.setAttribute(prop, String(active));
      }
    }
  }

  /** Keeps ?variant= accurate so a refresh or shared link restores the choice. */
  #syncUrl() {
    // The custom size has no variant, so leave the last real one in place
    // rather than stripping it.
    if (this.isCustomSize) return;

    const variantId = this.variantId;
    if (!variantId) return;

    const url = new URL(window.location.href);
    url.searchParams.set('variant', String(variantId));
    // Nothing here uses selling plans any more; clear a stale param if present.
    url.searchParams.delete('selling_plan');

    if (url.href !== window.location.href) {
      history.replaceState({}, '', url.toString());
    }
  }

  /**
   * Asks the theme to re-render for the newly selected variant.
   *
   * Product media is sorted server-side — Liquid moves the variant's image to
   * the front of the gallery — so there is no client-side way to change which
   * image is active. Horizon's media gallery listens for ProductSelectEvent and
   * swaps itself out using the HTML the event's promise resolves with, the same
   * path its own variant picker uses.
   *
   * Only a size change triggers this. Switching plan also changes the variant,
   * but every plan for a size shows the same room, so re-fetching there would
   * flip the image for no reason.
   */
  #syncProductMedia() {
    const section = this.closest('.shopify-section');
    if (!section?.querySelector('media-gallery')) return;

    const variantId = this.variantId;
    const productUrl = this.dataset.productUrl;
    if (!variantId || !productUrl) return;

    // Abandon an in-flight request so rapid clicking can't land out of order.
    this.#mediaController?.abort();
    this.#mediaController = new AbortController();

    const sectionId = this.dataset.sectionId;
    const url = sectionId
      ? `${productUrl}?section_id=${sectionId}&variant=${variantId}`
      : `${productUrl}?variant=${variantId}`;

    const deferred = ProductSelectEvent.createPromise();

    this.dispatchEvent(
      new ProductSelectEvent({
        product: {
          id: this.dataset.productId ?? '',
          title: this.dataset.productTitle ?? '',
          handle: this.dataset.productHandle ?? '',
        },
        selectedOptions: [],
        detail: { variantId: String(variantId) },
        promise: deferred.promise,
      })
    );

    fetch(url, { signal: this.#mediaController.signal })
      .then((response) => response.text())
      .then((text) => {
        const html = new DOMParser().parseFromString(text, 'text/html');
        deferred.resolve({
          variant: null,
          detail: {
            html,
            productId: this.dataset.productId ?? '',
            sourceId: 'plan-selector',
            resource: null,
          },
        });
      })
      .catch((error) => {
        if (error?.name === 'AbortError') return;
        console.warn('[plan-selector] Media update failed', error);
        // Resolve empty so listeners awaiting the promise don't hang.
        deferred.resolve({
          variant: null,
          detail: { html: null, productId: '', sourceId: 'plan-selector', resource: null },
        });
      });
  }
}

if (!customElements.get('plan-selector-component')) {
  customElements.define('plan-selector-component', PlanSelectorComponent);
}